import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import { useFormikContext } from 'formik';
import i18n from 'i18n-js';
import { DayKey } from 'components/restaurantInformation/components/types';
import {
    createEmptyReservationRequestDay,
    formatReservationStarts,
    getAvailableReservationStarts,
    getDayWorkingHoursLabel,
    hasWorkingHours,
    RESERVATION_REQUEST_DAY_KEYS,
    RequestWorkingHours,
} from 'helpers/reservationRequestTimesHelper';
import {
    ReservationRequestDaySettings,
    ReservationRequestTimes,
} from 'store/restaurantDetailsStore/reducer';

interface ReservationRequestTimesForm {
    requestTimesFollowDineInWorkingHours: boolean;
    requestTimes: ReservationRequestTimes | null;
    maxReservationDurationMinutes: number;
}

interface Props {
    workingHours: RequestWorkingHours;
    canEdit: boolean;
    isSubmitting: boolean;
    submitCount: number;
    validationErrors: Partial<Record<DayKey, string>>;
    warningErrors: Partial<Record<DayKey, string>>;
    warningDays: Partial<Record<DayKey, boolean>>;
    savedRequestTimes: ReservationRequestTimes | null;
    savedDurationMinutes: number;
    onModeChange: (follows: boolean) => void;
}

const timeOptions = Array.from({ length: 48 }, (_, index) => {
    const minutes = index * 30;
    return Math.floor(minutes / 60) + (minutes % 60) / 100;
});

function formatTime(value: number | null): string {
    if (value === null) return '';
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 100);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export default function ReservationRequestTimesSection({
    workingHours,
    canEdit,
    isSubmitting,
    submitCount,
    validationErrors,
    warningErrors,
    warningDays,
    savedRequestTimes,
    savedDurationMinutes,
    onModeChange,
}: Props) {
    const { values, setFieldValue } = useFormikContext<ReservationRequestTimesForm>();
    const requestTimes = values.requestTimes ?? {};
    const followsWorkingHours = values.requestTimesFollowDineInWorkingHours;
    const sourceDay = RESERVATION_REQUEST_DAY_KEYS.find(
        (day) =>
            hasWorkingHours(workingHours, day) &&
            requestTimes[day]?.isEnabled === true
    );

    function updateDay(day: DayKey, settings: ReservationRequestDaySettings) {
        setFieldValue('requestTimes', {
            ...requestTimes,
            [day]: settings,
        });
    }

    function updatePeriod(day: DayKey, index: number, field: 'from' | 'to', value: number | null) {
        const settings = requestTimes[day];
        if (!settings) return;
        const periods = settings.periods.map((period, periodIndex) =>
            periodIndex === index ? { ...period, [field]: value } : period
        );
        updateDay(day, { ...settings, periods });
    }

    function applySourceDay() {
        if (!sourceDay) return;
        const source = requestTimes[sourceDay];
        if (!source) return;
        const next = { ...requestTimes };
        RESERVATION_REQUEST_DAY_KEYS.forEach((day) => {
            if (
                day !== sourceDay &&
                hasWorkingHours(workingHours, day) &&
                next[day]?.isEnabled
            ) {
                next[day] = {
                    ...next[day]!,
                    periods: source.periods.map((period) => ({ ...period })),
                };
            }
        });
        setFieldValue('requestTimes', next);
    }

    return (
        <Box mt={3} id="requestTimes" tabIndex={-1}>
            <Typography variant="caption" display="block" gutterBottom>
                {i18n.t('restaurant.reservationSettings.requestTimesTitle').toUpperCase()}
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        color="primary"
                        checked={followsWorkingHours}
                        disabled={!canEdit || isSubmitting}
                        onChange={(_, checked) => onModeChange(checked)}
                    />
                }
                label={i18n.t(
                    'restaurant.reservationSettings.requestTimesFollowWorkingHours'
                )}
            />
            <Typography variant="body2" color="textSecondary" style={{ marginLeft: 32 }}>
                {i18n.t(
                    followsWorkingHours
                        ? 'restaurant.reservationSettings.requestTimesFollowDescription'
                        : 'restaurant.reservationSettings.requestTimesOwnDescription'
                )}
            </Typography>

            {!followsWorkingHours && (
                <>
                    {Object.values(warningDays).some(Boolean) && (
                        <Box
                            mt={2}
                            p={1.5}
                            bgcolor="#fff4e5"
                            borderRadius={4}
                            role="alert"
                        >
                            <Typography variant="body2" style={{ color: '#8a4b08' }}>
                                {i18n.t(
                                    'restaurant.reservationSettings.requestTimesHoursChanged'
                                )}
                            </Typography>
                        </Box>
                    )}
                    <Box mt={2}>
                        {RESERVATION_REQUEST_DAY_KEYS.map((day) => {
                            const hasHours = hasWorkingHours(workingHours, day);
                            const settings = requestTimes[day];
                            const isEnabled = settings?.isEnabled ?? false;
                            const starts = getAvailableReservationStarts(
                                day,
                                requestTimes,
                                false,
                                workingHours,
                                values.maxReservationDurationMinutes
                            );
                            const times = formatReservationStarts(starts);
                            const savedStarts = getAvailableReservationStarts(
                                day,
                                savedRequestTimes,
                                false,
                                workingHours,
                                savedDurationMinutes
                            );
                            const savedTimes = formatReservationStarts(savedStarts);

                            return (
                                <Box
                                    key={day}
                                    py={1.25}
                                    display="flex"
                                    alignItems="flex-start"
                                    gridGap={16}
                                    borderBottom={1}
                                    borderColor="grey.200"
                                    style={{
                                        opacity: warningDays[day] ? 0.8 : 1,
                                    }}
                                >
                                    <Box width={230} flexShrink={0}>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: warningDays[day] ? '#b76500' : undefined,
                                            }}
                                        >
                                            {i18n.t(`common.${day}`)}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {hasHours
                                                ? i18n.t(
                                                      'restaurant.reservationSettings.requestTimesDineIn',
                                                      {
                                                          hours: getDayWorkingHoursLabel(
                                                              workingHours,
                                                              day
                                                          ),
                                                      }
                                                  )
                                                : i18n.t(
                                                      'restaurant.reservationSettings.requestTimesClosed'
                                                  )}
                                        </Typography>
                                    </Box>
                                    {!hasHours ? (
                                        <Box>
                                            <Typography
                                                  variant="caption"
                                                  color={
                                                      warningDays[day]
                                                          ? 'error'
                                                          : 'textSecondary'
                                                  }
                                                  style={{
                                                      color: warningDays[day]
                                                          ? '#b76500'
                                                          : undefined,
                                                  }}
                                            >
                                                  {i18n.t(
                                                      'restaurant.reservationSettings.requestTimesNoHours'
                                                  )}
                                            </Typography>
                                            {warningDays[day] && warningErrors[day] && (
                                                  <Typography
                                                      variant="caption"
                                                      display="block"
                                                      style={{
                                                          color: '#b76500',
                                                          whiteSpace: 'pre-line',
                                                      }}
                                                  >
                                                      {warningErrors[day]}
                                                  </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box flex={1}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                flexWrap="wrap"
                                                gridGap={8}
                                            >
                                                <Switch
                                                    color="primary"
                                                    checked={isEnabled}
                                                    disabled={!canEdit || isSubmitting}
                                                    onChange={(_, checked) => {
                                                        const current =
                                                            settings ??
                                                            createEmptyReservationRequestDay();
                                                        const periods =
                                                            checked && current.periods.length === 0
                                                                ? [{ from: null, to: null }]
                                                                : current.periods;
                                                        updateDay(day, {
                                                            ...current,
                                                            isEnabled: checked,
                                                            periods,
                                                        });
                                                    }}
                                                    inputProps={{
                                                        'aria-label': i18n.t(
                                                            `common.${day}`
                                                        ),
                                                    }}
                                                />
                                                {!isEnabled ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {i18n.t(
                                                            'restaurant.reservationSettings.requestTimesDayOff'
                                                        )}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        {(settings?.periods ?? []).map(
                                                            (period, index) => (
                                                                <Box
                                                                    key={`${day}-${index}`}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    flexWrap="wrap"
                                                                    gridGap={6}
                                                                >
                                                                    <TextField
                                                                        select
                                                                        id={`request-times-${day}-${index}-from`}
                                                                        variant="filled"
                                                                        label={i18n.t(
                                                                            'restaurant.reservationSettings.requestTimesFirstStart'
                                                                        )}
                                                                        value={period.from ?? ''}
                                                                        disabled={
                                                                            !canEdit ||
                                                                            isSubmitting
                                                                        }
                                                                        onChange={(event) =>
                                                                            updatePeriod(
                                                                                day,
                                                                                index,
                                                                                'from',
                                                                                event.target
                                                                                    .value === ''
                                                                                    ? null
                                                                                    : Number(
                                                                                          event
                                                                                              .target
                                                                                              .value
                                                                                      )
                                                                            )
                                                                        }
                                                                        style={{ width: 130 }}
                                                                    >
                                                                        <MenuItem value="">
                                                                            —
                                                                        </MenuItem>
                                                                        {timeOptions.map(
                                                                            (time) => (
                                                                                <MenuItem
                                                                                    key={time}
                                                                                    value={time}
                                                                                >
                                                                                    {formatTime(
                                                                                        time
                                                                                    )}
                                                                                </MenuItem>
                                                                            )
                                                                        )}
                                                                    </TextField>
                                                                    <Typography>–</Typography>
                                                                    <TextField
                                                                        select
                                                                        id={`request-times-${day}-${index}-to`}
                                                                        variant="filled"
                                                                        label={i18n.t(
                                                                            'restaurant.reservationSettings.requestTimesLastStart'
                                                                        )}
                                                                        value={period.to ?? ''}
                                                                        disabled={
                                                                            !canEdit ||
                                                                            isSubmitting
                                                                        }
                                                                        onChange={(event) =>
                                                                            updatePeriod(
                                                                                day,
                                                                                index,
                                                                                'to',
                                                                                event.target
                                                                                    .value === ''
                                                                                    ? null
                                                                                    : Number(
                                                                                          event
                                                                                              .target
                                                                                              .value
                                                                                      )
                                                                            )
                                                                        }
                                                                        style={{ width: 130 }}
                                                                    >
                                                                        <MenuItem value="">
                                                                            —
                                                                        </MenuItem>
                                                                        {timeOptions.map(
                                                                            (time) => (
                                                                                <MenuItem
                                                                                    key={time}
                                                                                    value={time}
                                                                                >
                                                                                    {formatTime(
                                                                                        time
                                                                                    )}
                                                                                </MenuItem>
                                                                            )
                                                                        )}
                                                                    </TextField>
                                                                    <Button
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={
                                                                            !canEdit ||
                                                                            isSubmitting
                                                                        }
                                                                        onClick={() =>
                                                                            updateDay(day, {
                                                                                ...settings!,
                                                                                periods:
                                                                                    settings!.periods.filter(
                                                                                        (
                                                                                            _,
                                                                                            periodIndex
                                                                                        ) =>
                                                                                            periodIndex !==
                                                                                            index
                                                                                    ),
                                                                            })
                                                                        }
                                                                    >
                                                                        −{' '}
                                                                        {i18n
                                                                            .t(
                                                                                'restaurant.reservationSettings.requestTimesRemove'
                                                                            )
                                                                            .toUpperCase()}
                                                                    </Button>
                                                                </Box>
                                                            )
                                                        )}
                                                        <Button
                                                            size="small"
                                                            color="primary"
                                                            disabled={
                                                                !canEdit || isSubmitting
                                                            }
                                                            onClick={() =>
                                                                updateDay(day, {
                                                                    ...settings!,
                                                                    periods: [
                                                                        ...settings!.periods,
                                                                        {
                                                                            from: null,
                                                                            to: null,
                                                                        },
                                                                    ],
                                                                })
                                                            }
                                                        >
                                                            +{' '}
                                                            {i18n
                                                                .t(
                                                                    'restaurant.reservationSettings.requestTimesAdd'
                                                                )
                                                                .toUpperCase()}
                                                        </Button>
                                                    </>
                                                )}
                                            </Box>
                                            {isEnabled && times && (
                                                <Typography
                                                    variant="caption"
                                                    color="primary"
                                                    display="block"
                                                    style={{ marginTop: 6 }}
                                                >
                                                    {i18n.t(
                                                        'restaurant.reservationSettings.requestTimesPreview',
                                                        { times, count: starts.length }
                                                    )}
                                                </Typography>
                                            )}
                                            {isEnabled &&
                                                submitCount > 0 &&
                                                validationErrors[day] && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                        display="block"
                                                        style={{ whiteSpace: 'pre-line' }}
                                                    >
                                                        {validationErrors[day]}
                                                    </Typography>
                                                )}
                                            {warningDays[day] && savedTimes && (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    style={{ color: '#b76500' }}
                                                >
                                                    {i18n.t(
                                                        'restaurant.reservationSettings.requestTimesCurrentPreview',
                                                        {
                                                            times: savedTimes,
                                                            count: savedStarts.length,
                                                        }
                                                    )}
                                                </Typography>
                                            )}
                                            {warningDays[day] && warningErrors[day] && (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    style={{
                                                        color: '#b76500',
                                                        whiteSpace: 'pre-line',
                                                    }}
                                                >
                                                    {warningErrors[day]}
                                                </Typography>
                                            )}
                                            {warningDays[day] && !savedTimes && (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    style={{ color: '#b76500' }}
                                                >
                                                    {i18n.t(
                                                        'restaurant.reservationSettings.requestTimesNoAvailableStarts'
                                                    )}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                    {sourceDay && (
                        <Button
                            color="primary"
                            disabled={!canEdit || isSubmitting}
                            onClick={applySourceDay}
                            style={{ marginTop: 8 }}
                        >
                            {i18n
                                .t('restaurant.reservationSettings.requestTimesApplyDay', {
                                    day: i18n.t(`common.${sourceDay}`).toUpperCase(),
                                })
                                .toUpperCase()}
                        </Button>
                    )}
                </>
            )}
        </Box>
    );
}
